/* eslint-disable react/destructuring-assignment */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import styled from 'styled-components'

import * as Sentry from '@sentry/react'

import backgroundImage from '~/shared/assets/background/circle-participation.png'
import errorImage from '~/shared/assets/images/error.png'
import theme from '~/shared/styles/theme'

interface Props {
  children?: ReactNode
}

interface State {
  hasError: boolean
  error: string
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = { hasError: false, error: '' }
  }

  static getDerivedStateFromError(error: State) {
    return { hasError: true, error: error.error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ hasError: true, error: error.message })
    // 여기서 에러를 catch 한 뒤 Sentry로 보낸다.
    if (import.meta.env.NODE_ENV === 'production') {
      Sentry.withScope((scope) => {
        scope.setExtra('componentStack', errorInfo)
        Sentry.captureException(error)
      })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Wrapper $backgroundImage={backgroundImage}>
          <ErrorImage src={errorImage} alt="error" />
          <Title>알 수 없는 에러가 발생했어요.</Title>
          <Desc>Error Message: {this.state.error || '알 수 없음'}</Desc>
        </Wrapper>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary

const Wrapper = styled.section<{
  $backgroundImage: string
}>`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;
  min-height: 100vh;

  background-image: url(${backgroundImage});
  background-repeat: repeat repeat;
  background-size: 100vw auto;

  @media ${theme.device.medium} {
    width: 100%;
    height: auto;
  }
`

const ErrorImage = styled.img`
  width: 300px;
  margin-bottom: 40px;
`

const Title = styled.h1`
  ${theme.fontStyle.title};

  @media ${theme.device.medium} {
    ${theme.fontStyle.titleMobile};
  }
`
const Desc = styled.p`
  ${theme.fontStyle.text};
  margin-top: 20px;
  color: ${theme.colors.grey_4};

  @media ${theme.device.medium} {
    ${theme.fontStyle.textMobile};
  }
`
